// Blog / Dental Guides content (EN/FR/DE). External-brand references intentionally
// excluded. Content is educational and avoids unverified savings/accreditation claims.

export const ARTICLES = [
  {
    key: "dental-implants-vietnam-guide",
    slug: {
      en: "dental-implants-vietnam-guide",
      fr: "guide-implants-dentaires-vietnam",
      de: "ratgeber-zahnimplantate-vietnam",
    },
    image:
      "https://images.unsplash.com/photo-1606811841689-23dfddce3e95?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    category: { en: "Implants", fr: "Implants", de: "Implantate" },
    readTime: 9,
    published: "2026-01-05",
    updated: "2026-01-05",
    author: "Dentel Vietnam Editorial Team",
    reviewer: "Pending independent medical review",
    title: {
      en: "Dental Implants in Vietnam: The Definitive Guide for International Patients (2026)",
      fr: "Implants dentaires au Vietnam : le guide de référence pour les patients internationaux (2026)",
      de: "Zahnimplantate in Vietnam: Der umfassende Ratgeber für internationale Patienten (2026)",
    },
    excerpt: {
      en: "A clear, independent overview of dental implants in Vietnam for international patients — how treatment works, what to check, and how to plan your visit.",
      fr: "Un aperçu clair et indépendant des implants dentaires au Vietnam pour les patients internationaux — déroulement, points à vérifier et organisation du séjour.",
      de: "Ein klarer, unabhängiger Überblick über Zahnimplantate in Vietnam — Ablauf, Prüfpunkte und Reiseplanung für internationale Patienten.",
    },
    metaTitle: {
      en: "Dental Implants in Vietnam: Definitive Guide 2026 | Dentel Vietnam",
      fr: "Implants dentaires au Vietnam : guide de référence 2026 | Dentel Vietnam",
      de: "Zahnimplantate in Vietnam: Ratgeber 2026 | Dentel Vietnam",
    },
    metaDesc: {
      en: "The definitive 2026 guide to dental implants in Vietnam for international patients: process, what to check, timelines, costs and how to plan safely.",
      fr: "Le guide de référence 2026 des implants dentaires au Vietnam : processus, vérifications, délais, coûts et organisation en toute sérénité.",
      de: "Der Ratgeber 2026 zu Zahnimplantaten in Vietnam: Prozess, Prüfpunkte, Zeitpläne, Kosten und sichere Planung.",
    },
    keyTakeaways: {
      en: [
        "Dental implants replace missing teeth with a titanium root that supports a crown, bridge or full arch.",
        "Safety and quality depend on the clinic and dentist — not the country. Verify licence, credentials and materials.",
        "Implants usually heal over several months, so treatment may need more than one trip to Vietnam.",
        "We do not publish fixed prices; request an indicative estimate for your specific case.",
        "Only a licensed dentist can confirm suitability after an in-person exam and imaging.",
      ],
      fr: [
        "Les implants remplacent les dents manquantes par une racine en titane soutenant couronne, bridge ou arcade.",
        "La sécurité et la qualité dépendent de la clinique et du dentiste, pas du pays. Vérifiez licence, diplômes et matériaux.",
        "Les implants cicatrisent souvent sur plusieurs mois : le traitement peut nécessiter plusieurs voyages.",
        "Nous ne publions pas de prix fixes ; demandez une estimation indicative pour votre cas.",
        "Seul un dentiste agréé peut confirmer l’indication après un examen en personne et une imagerie.",
      ],
      de: [
        "Implantate ersetzen fehlende Zähne durch eine Titanwurzel, die Krone, Brücke oder Bogen trägt.",
        "Sicherheit und Qualität hängen von Klinik und Zahnarzt ab, nicht vom Land. Prüfen Sie Lizenz, Qualifikationen und Materialien.",
        "Implantate heilen oft über mehrere Monate — die Behandlung kann mehrere Vietnam-Reisen erfordern.",
        "Wir veröffentlichen keine festen Preise; fordern Sie einen Richtwert für Ihren Fall an.",
        "Nur ein lizenzierter Zahnarzt kann die Eignung nach persönlicher Untersuchung und Bildgebung bestätigen.",
      ],
    },
    sections: [
      {
        id: "what-are-implants",
        h: {
          en: "What are dental implants?",
          fr: "Que sont les implants dentaires ?",
          de: "Was sind Zahnimplantate?",
        },
        body: {
          en: [
            "A dental implant is a small titanium post surgically placed into the jawbone to act as an artificial tooth root. Once it integrates with the bone, it can support a crown, bridge or full arch of teeth.",
            "Implants are widely used to replace missing teeth and are often chosen for their stability and natural feel. Whether they are suitable for you depends on your bone, gums and general health — something only a licensed dentist can determine in person.",
          ],
          fr: [
            "Un implant dentaire est une petite vis en titane insérée chirurgicalement dans l’os de la mâchoire pour servir de racine artificielle. Une fois intégré à l’os, il peut soutenir une couronne, un bridge ou une arcade complète.",
            "Les implants sont largement utilisés pour remplacer des dents manquantes, appréciés pour leur stabilité et leur aspect naturel. Leur indication dépend de votre os, de vos gencives et de votre santé — ce que seul un dentiste agréé peut évaluer en personne.",
          ],
          de: [
            "Ein Zahnimplantat ist eine kleine Titanschraube, die chirurgisch in den Kieferknochen eingesetzt wird und als künstliche Zahnwurzel dient. Nach dem Einwachsen trägt sie eine Krone, Brücke oder einen ganzen Bogen.",
            "Implantate werden häufig für fehlende Zähne genutzt und wegen ihrer Stabilität und ihres natürlichen Gefühls gewählt. Die Eignung hängt von Knochen, Zahnfleisch und Gesundheit ab — das kann nur ein lizenzierter Zahnarzt persönlich beurteilen.",
          ],
        },
      },
      {
        id: "why-vietnam",
        h: {
          en: "Why do patients consider Vietnam?",
          fr: "Pourquoi les patients envisagent-ils le Vietnam ?",
          de: "Warum ziehen Patienten Vietnam in Betracht?",
        },
        body: {
          en: [
            "Vietnam has become a destination some international patients explore for dental care. Selected providers may offer modern facilities, experienced dentists and internationally recognised implant systems — though this varies significantly between clinics.",
            "Treatment costs may be lower than in some countries, and Vietnam’s appeal as a travel destination allows patients to combine care with recovery time. We avoid publishing specific savings figures, as real costs depend entirely on your individual case.",
          ],
          fr: [
            "Le Vietnam est devenu une destination explorée par certains patients internationaux. Des prestataires sélectionnés peuvent proposer des installations modernes, des dentistes expérimentés et des systèmes d’implants reconnus — mais cela varie fortement d’une clinique à l’autre.",
            "Les coûts peuvent être inférieurs à ceux de certains pays, et l’attrait touristique du Vietnam permet d’associer soins et récupération. Nous ne publions pas de chiffres d’économies, car les coûts réels dépendent entièrement de votre cas.",
          ],
          de: [
            "Vietnam ist ein Ziel geworden, das manche internationalen Patienten für Zahnbehandlungen erkunden. Ausgewählte Anbieter bieten möglicherweise moderne Einrichtungen, erfahrene Zahnärzte und anerkannte Implantatsysteme — das variiert jedoch stark je nach Klinik.",
            "Die Kosten können niedriger sein als in einigen Ländern, und Vietnams Reiz als Reiseziel erlaubt es, Behandlung und Erholung zu verbinden. Wir veröffentlichen keine konkreten Ersparniszahlen, da die tatsächlichen Kosten von Ihrem Fall abhängen.",
          ],
        },
      },
      {
        id: "the-process",
        h: {
          en: "The implant treatment process",
          fr: "Le déroulement du traitement implantaire",
          de: "Der Ablauf der Implantatbehandlung",
        },
        body: {
          en: [
            "Implant treatment usually begins with a consultation and imaging such as a CBCT scan, followed by placement of the implant. A healing period allows the implant to integrate with the bone before the final crown, bridge or denture is attached.",
            "Because healing can take several months, some international patients complete treatment over more than one visit. Your dentist will explain the timeline and whether any additional procedures, such as bone grafting, may be needed.",
          ],
          fr: [
            "Le traitement commence généralement par une consultation et une imagerie (comme un scanner CBCT), suivies de la pose de l’implant. Une période de cicatrisation permet l’ostéo-intégration avant la pose de la couronne, du bridge ou de la prothèse finale.",
            "La cicatrisation pouvant durer plusieurs mois, certains patients réalisent le traitement en plusieurs voyages. Votre dentiste précisera le calendrier et l’éventuel besoin de procédures complémentaires, comme une greffe osseuse.",
          ],
          de: [
            "Die Behandlung beginnt meist mit einer Beratung und Bildgebung (z. B. CBCT-Scan), gefolgt vom Einsetzen des Implantats. Eine Einheilphase ermöglicht das Einwachsen, bevor die finale Krone, Brücke oder Prothese angebracht wird.",
            "Da die Heilung mehrere Monate dauern kann, führen manche Patienten die Behandlung über mehrere Besuche durch. Ihr Zahnarzt erläutert den Zeitplan und ob zusätzliche Eingriffe wie ein Knochenaufbau nötig sind.",
          ],
        },
      },
      {
        id: "what-to-check",
        h: {
          en: "What to check before you choose a clinic",
          fr: "Que vérifier avant de choisir une clinique",
          de: "Was Sie vor der Klinikwahl prüfen sollten",
        },
        body: {
          en: [
            "Before committing to any clinic, verify its licence, the treating dentist’s credentials and their experience with implant cases. Ask which implant brand will be used, how instruments are sterilised, and what diagnostic technology is available.",
            "Request a written treatment plan with clear costs, warranty terms and an aftercare plan. A trustworthy provider will answer these questions openly and will not pressure you into a quick decision.",
          ],
          fr: [
            "Avant de vous engager, vérifiez la licence de la clinique, les diplômes du dentiste et son expérience en implantologie. Demandez la marque d’implant utilisée, la méthode de stérilisation et la technologie de diagnostic disponible.",
            "Demandez un plan de traitement écrit avec des coûts clairs, des conditions de garantie et un plan de suivi. Un prestataire de confiance répondra ouvertement et ne vous poussera pas à décider dans l’urgence.",
          ],
          de: [
            "Prüfen Sie vor der Entscheidung die Lizenz der Klinik, die Qualifikationen des Zahnarztes und dessen Erfahrung mit Implantaten. Fragen Sie nach der Implantatmarke, der Sterilisation der Instrumente und der verfügbaren Diagnosetechnik.",
            "Verlangen Sie einen schriftlichen Behandlungsplan mit klaren Kosten, Garantiebedingungen und Nachsorge. Ein vertrauenswürdiger Anbieter beantwortet dies offen und drängt Sie nicht zu einer schnellen Entscheidung.",
          ],
        },
      },
      {
        id: "planning-visit",
        h: {
          en: "Planning your visit",
          fr: "Organiser votre séjour",
          de: "Ihren Besuch planen",
        },
        body: {
          en: [
            "Send recent dental photos or X-rays for an initial, informal review to understand your possible options. Plan your stay around the treatment timeline your provider outlines, and allow time for follow-up appointments.",
            "Remember that any online review is not a diagnosis. A licensed dentist must examine you in person before confirming your treatment and its suitability.",
          ],
          fr: [
            "Envoyez des photos ou radiographies récentes pour une première évaluation informelle afin de comprendre vos options. Organisez votre séjour selon le calendrier indiqué par le prestataire et prévoyez du temps pour les rendez-vous de suivi.",
            "Rappelez-vous qu’une évaluation en ligne n’est pas un diagnostic. Un dentiste agréé doit vous examiner en personne avant de confirmer le traitement et son indication.",
          ],
          de: [
            "Senden Sie aktuelle Fotos oder Röntgenbilder für eine erste, informelle Prüfung, um Ihre Optionen zu verstehen. Planen Sie Ihren Aufenthalt nach dem Zeitplan des Anbieters und lassen Sie Zeit für Nachsorgetermine.",
            "Beachten Sie: Eine Online-Prüfung ist keine Diagnose. Ein lizenzierter Zahnarzt muss Sie persönlich untersuchen, bevor Behandlung und Eignung bestätigt werden.",
          ],
        },
      },
    ],
    references: [
      { text: "World Health Organization — Oral health", url: "https://www.who.int/health-topics/oral-health" },
      { text: "FDI World Dental Federation", url: "https://www.fdiworlddental.org/" },
    ],
    relatedTreatments: ["dental-implants", "all-on-4", "all-on-6"],
    relatedArticles: ["dental-implants-vietnam-safety"],
  },

  {
    key: "dental-implants-vietnam-safety",
    slug: {
      en: "dental-implants-vietnam-safety",
      fr: "securite-implants-dentaires-vietnam",
      de: "sicherheit-zahnimplantate-vietnam",
    },
    image:
      "https://images.pexels.com/photos/6812507/pexels-photo-6812507.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
    category: { en: "Safety", fr: "Sécurité", de: "Sicherheit" },
    readTime: 7,
    published: "2026-01-05",
    updated: "2026-01-05",
    author: "Dentel Vietnam Editorial Team",
    reviewer: "Pending independent medical review",
    title: {
      en: "Are Dental Implants in Vietnam Safe? A Guide for International Patients",
      fr: "Les implants dentaires au Vietnam sont-ils sûrs ? Guide pour patients internationaux",
      de: "Sind Zahnimplantate in Vietnam sicher? Ein Ratgeber für internationale Patienten",
    },
    excerpt: {
      en: "Safety depends on the clinic, not the country. Here is a practical, independent framework for judging quality and reducing risk.",
      fr: "La sécurité dépend de la clinique, pas du pays. Voici un cadre pratique et indépendant pour juger la qualité et réduire les risques.",
      de: "Sicherheit hängt von der Klinik ab, nicht vom Land. Hier ist ein praktischer, unabhängiger Leitfaden zur Beurteilung der Qualität.",
    },
    metaTitle: {
      en: "Are Dental Implants in Vietnam Safe? | Dentel Vietnam",
      fr: "Les implants dentaires au Vietnam sont-ils sûrs ? | Dentel Vietnam",
      de: "Sind Zahnimplantate in Vietnam sicher? | Dentel Vietnam",
    },
    metaDesc: {
      en: "An independent guide to dental implant safety in Vietnam: how to assess a clinic, questions to ask and how to reduce risk as an international patient.",
      fr: "Un guide indépendant sur la sécurité des implants au Vietnam : évaluer une clinique, questions à poser et réduction des risques.",
      de: "Ein unabhängiger Leitfaden zur Sicherheit von Zahnimplantaten in Vietnam: Klinik beurteilen, Fragen stellen und Risiken senken.",
    },
    keyTakeaways: {
      en: [
        "Safety depends on the specific clinic and dentist, not on the country.",
        "Verify the clinic licence, the dentist’s credentials and their implant experience.",
        "Ask about sterilisation, the implant brand and materials, and available imaging.",
        "Insist on a written treatment plan with clear costs, warranty and aftercare.",
        "Careful research reduces risk — but no procedure is risk-free or guaranteed.",
      ],
      fr: [
        "La sécurité dépend de la clinique et du dentiste, pas du pays.",
        "Vérifiez la licence de la clinique, les diplômes du dentiste et son expérience en implantologie.",
        "Renseignez-vous sur la stérilisation, la marque d’implant, les matériaux et l’imagerie disponible.",
        "Exigez un plan de traitement écrit avec coûts clairs, garantie et suivi.",
        "Une recherche soignée réduit le risque — mais aucune intervention n’est sans risque ni garantie.",
      ],
      de: [
        "Sicherheit hängt von der konkreten Klinik und dem Zahnarzt ab, nicht vom Land.",
        "Prüfen Sie Kliniklizenz, Qualifikationen des Zahnarztes und dessen Implantaterfahrung.",
        "Fragen Sie nach Sterilisation, Implantatmarke, Materialien und verfügbarer Bildgebung.",
        "Bestehen Sie auf schriftlichem Behandlungsplan mit klaren Kosten, Garantie und Nachsorge.",
        "Sorgfältige Recherche senkt das Risiko — kein Eingriff ist jedoch risikofrei oder garantiert.",
      ],
    },
    sections: [
      {
        id: "safety-depends",
        h: {
          en: "Safety depends on the clinic, not the country",
          fr: "La sécurité dépend de la clinique, pas du pays",
          de: "Sicherheit hängt von der Klinik ab, nicht vom Land",
        },
        body: {
          en: [
            "It is a mistake to judge safety by country alone. Excellent and poor clinics exist everywhere. The most useful approach is to assess the specific clinic, the dentist and the standards they follow.",
            "This guide gives you a practical framework of questions and checks so you can make an informed decision — wherever you choose to have treatment.",
          ],
          fr: [
            "Juger la sécurité au seul niveau du pays est une erreur. Il existe d’excellentes et de mauvaises cliniques partout. Le plus utile est d’évaluer la clinique, le dentiste et les standards suivis.",
            "Ce guide propose un cadre pratique de questions et de vérifications pour décider en connaissance de cause — où que vous choisissiez de vous faire soigner.",
          ],
          de: [
            "Sicherheit allein nach Land zu beurteilen, ist ein Fehler. Gute und schlechte Kliniken gibt es überall. Am sinnvollsten ist es, die konkrete Klinik, den Zahnarzt und die Standards zu bewerten.",
            "Dieser Leitfaden bietet einen praktischen Rahmen aus Fragen und Prüfungen, damit Sie eine fundierte Entscheidung treffen können — wo auch immer Sie sich behandeln lassen.",
          ],
        },
      },
      {
        id: "credentials",
        h: {
          en: "Licence, credentials and experience",
          fr: "Licence, diplômes et expérience",
          de: "Lizenz, Qualifikationen und Erfahrung",
        },
        body: {
          en: [
            "Confirm that the clinic is licensed and that your treating dentist is qualified and experienced in implant treatment. Ask how many similar cases they handle and whether a specialist places the implants.",
            "A reputable provider will be transparent about credentials and happy to answer these questions.",
          ],
          fr: [
            "Confirmez que la clinique est agréée et que votre dentiste est qualifié et expérimenté en implantologie. Demandez combien de cas similaires il traite et si un spécialiste pose les implants.",
            "Un prestataire sérieux sera transparent sur ses diplômes et répondra volontiers à ces questions.",
          ],
          de: [
            "Bestätigen Sie, dass die Klinik lizenziert und Ihr Zahnarzt qualifiziert und erfahren in der Implantologie ist. Fragen Sie, wie viele ähnliche Fälle er behandelt und ob ein Spezialist die Implantate setzt.",
            "Ein seriöser Anbieter ist bei Qualifikationen transparent und beantwortet diese Fragen gern.",
          ],
        },
      },
      {
        id: "hygiene-materials",
        h: {
          en: "Sterilisation, materials and technology",
          fr: "Stérilisation, matériaux et technologie",
          de: "Sterilisation, Materialien und Technik",
        },
        body: {
          en: [
            "Ask about sterilisation procedures and infection control. Confirm which implant brand and materials will be used, and whether they are internationally recognised systems with available documentation.",
            "Diagnostic technology such as CBCT imaging supports accurate planning. Availability varies between clinics, so confirm what the provider uses.",
          ],
          fr: [
            "Renseignez-vous sur les procédures de stérilisation et le contrôle des infections. Confirmez la marque d’implant et les matériaux utilisés, et s’il s’agit de systèmes reconnus avec documentation disponible.",
            "Une technologie de diagnostic comme le CBCT facilite une planification précise. La disponibilité varie ; confirmez ce que le prestataire utilise.",
          ],
          de: [
            "Fragen Sie nach Sterilisationsverfahren und Infektionskontrolle. Bestätigen Sie Implantatmarke und Materialien und ob es anerkannte Systeme mit verfügbarer Dokumentation sind.",
            "Diagnosetechnik wie CBCT unterstützt eine präzise Planung. Die Verfügbarkeit variiert; bestätigen Sie, was der Anbieter nutzt.",
          ],
        },
      },
      {
        id: "plan-warranty",
        h: {
          en: "Treatment plan, warranty and aftercare",
          fr: "Plan de traitement, garantie et suivi",
          de: "Behandlungsplan, Garantie und Nachsorge",
        },
        body: {
          en: [
            "Insist on a written treatment plan with clear costs. Understand the warranty terms for implants and restorations, and how aftercare works once you return home.",
            "Knowing who to contact and what is covered if a problem arises is an important part of feeling confident about your treatment.",
          ],
          fr: [
            "Exigez un plan de traitement écrit avec des coûts clairs. Comprenez les conditions de garantie des implants et des restaurations, ainsi que le suivi une fois rentré chez vous.",
            "Savoir qui contacter et ce qui est couvert en cas de problème est essentiel pour aborder votre traitement sereinement.",
          ],
          de: [
            "Bestehen Sie auf einem schriftlichen Behandlungsplan mit klaren Kosten. Verstehen Sie die Garantiebedingungen für Implantate und Restaurationen sowie die Nachsorge nach Ihrer Rückkehr.",
            "Zu wissen, wen Sie kontaktieren und was bei Problemen abgedeckt ist, ist wichtig für Ihr Vertrauen in die Behandlung.",
          ],
        },
      },
    ],
    references: [
      { text: "World Health Organization — Oral health", url: "https://www.who.int/health-topics/oral-health" },
      { text: "FDI World Dental Federation", url: "https://www.fdiworlddental.org/" },
    ],
    relatedTreatments: ["dental-implants", "all-on-4"],
    relatedArticles: ["dental-implants-vietnam-guide"],
  },
];

export const getArticle = (key) => ARTICLES.find((a) => a.key === key);
export const getArticleBySlug = (slug, lang) =>
  ARTICLES.find((a) => a.slug[lang] === slug || Object.values(a.slug).includes(slug));
